import { MailDelivery, ESnapshotExists, ENoticeDraft } from 'lib/types';
import { MailedNoticeDocumentType, ShippingTier } from 'lib/types/mail';
import {
  MAX_MAIL_COPIES,
  getMailDeliveryValidationError
} from 'lib/mail/validation';
import {
  AffidavitType,
  ShippingSpeed,
  shippingTierToSpeed
} from '../DeliveryAddOns/pricingConfig';
import { verifyAddressDeliverability } from '../helpers/verifyAddressDeliverability';

export const DEFAULT_MAILING: MailDelivery = {
  address: {
    address_line1: '',
    address_line2: '',
    address_city: '',
    address_state: '',
    address_zip: ''
  },
  copies: 1,
  description: '',
  name: '',
  isCourthouse: false,
  provider: 'lob'
};

export const DEFAULT_COURTHOUSE: MailDelivery = {
  ...DEFAULT_MAILING,
  isCourthouse: true
};

export const DEFAULT_ERROR: string[] = Array(5).fill('');

export { MAX_MAIL_COPIES };

export function emptyErrors(count: number): string[] {
  return Array(count).fill('');
}

// Canonical check for whether a MailDelivery entry has user-entered address data
// (i.e. is not a blank placeholder). Checks address_line1 only — full field
// validation (name, city, zip, etc.) is handled by getRecipientValidation.
// Use this instead of columnObjectsAreEqual(r, DEFAULT_MAILING) — that comparison
// breaks when the placeholder is stamped with a non-default provider (e.g. 'manual'
// for wet-sig orgs).
export const hasPopulatedAddress = (r: MailDelivery): boolean =>
  (r.address?.address_line1 ?? '').trim() !== '';

export type AffidavitRecipientsFormState = {
  sendAffidavitByEmail: boolean;
  sendAffidavitByMail: boolean;
  mailingRecipients: MailDelivery[];
  recipientValidationErrors: string[];
  sendAffidavitToCourthouse: boolean;
  courthouseRecipients: MailDelivery[];
  courthouseValidationErrors: string[];
  showErrors: boolean;
  // Add-on selections (flag-gated)
  affidavitType: AffidavitType | null;
  shippingSpeed: ShippingSpeed;
  deliveryOption: 'email' | 'mail';
  /**
   * Whether the customer has opted into a paid affidavit add-on on top of the
   * one included in the notice cost. Backs the Yes/No gate for paper types where
   * the extra affidavit is optional; ignored for papers that mandate a mailed
   * affidavit (the address stays visible regardless).
   */
  wantsAdditionalAffidavit: boolean;
  /** Publisher-funded presets: displayed and gated on, never submitted. */
  noticeTypeDefaultRecipients: MailDelivery[];
  invoiceMailingRecipients: MailDelivery[];
  invoiceRecipientValidationErrors: string[];
};

/**
 * Single source of truth for "does this affidavit selection require a mailing
 * address". True for a wet-signature affidavit (a mandatory first copy on a
 * wet-sig-required paper, or an opted-in wet-sig copy) or when mail delivery was
 * chosen. Deliberately NOT gated on the additional-affidavit opt-in — a mandatory
 * wet-sig copy always needs an address. Keep completeness, submit validation, and
 * the section UI in agreement by calling this rather than re-deriving the rule.
 */
export function affidavitAddressRequired(
  affidavitType: AffidavitType | null,
  deliveryOption: 'email' | 'mail'
): boolean {
  return affidavitType === 'wet_sig' || deliveryOption === 'mail';
}

export function affidavitMailStamp(affidavitType: AffidavitType): {
  documentType: MailedNoticeDocumentType;
  provider: MailDelivery['provider'];
} {
  if (affidavitType === 'wet_sig') {
    return {
      documentType: MailedNoticeDocumentType.WET_SIG_AFFIDAVIT,
      provider: 'manual'
    };
  }
  if (affidavitType === 'ron') {
    return {
      documentType: MailedNoticeDocumentType.RON_AFFIDAVIT,
      provider: 'lob'
    };
  }
  return {
    documentType: MailedNoticeDocumentType.STANDARD_AFFIDAVIT,
    provider: 'lob'
  };
}

export type AffidavitAddOnPrefill = {
  affidavitType: AffidavitType | null;
  /** Null when no tier was stored (leave the current default in place). */
  shippingSpeed: ShippingSpeed | null;
  deliveryOption: 'email' | 'mail';
  wantsAdditionalAffidavit: boolean;
};

/**
 * Derives the add-on opt-in UI state from the affidavit mail records saved on an
 * existing notice (invoice and courthouse records already removed), for
 * prefilling the edit / confirm-proof flow.
 *
 * The opt-in ("I would like more than one" / additional affidavit) reflects a
 * *paid* extra on top of the affidavit included in the notice cost — not merely
 * the presence of a mailing address:
 *  - Wet-sig-required papers always store the mandatory, free first copy with an
 *    address, so a single ground copy is NOT an opt-in. Only additional copies or
 *    an upgraded shipping tier count.
 *  - Papers that don't mandate a mailed affidavit include an emailed copy, so any
 *    populated affidavit address is a paid add-on the customer opted into.
 */
export function deriveAffidavitAddOnPrefill(
  affidavitMail: MailDelivery[],
  wetSigRequired: boolean
): AffidavitAddOnPrefill {
  const wetSigItems = affidavitMail.filter(
    m => m.documentType === MailedNoticeDocumentType.WET_SIG_AFFIDAVIT
  );
  const ronItems = affidavitMail.filter(
    m => m.documentType === MailedNoticeDocumentType.RON_AFFIDAVIT
  );

  let affidavitType: AffidavitType | null = null;
  let shippingSpeed: ShippingSpeed | null = null;
  if (wetSigItems.length) {
    affidavitType = 'wet_sig';
    const tier = wetSigItems.find(m => m.shippingTier)?.shippingTier;
    if (tier) {
      shippingSpeed = shippingTierToSpeed(tier);
    }
  } else if (ronItems.length) {
    affidavitType = 'ron';
  }

  const hasPopulated = affidavitMail.some(hasPopulatedAddress);

  let wantsAdditionalAffidavit: boolean;
  if (wetSigRequired) {
    const totalWetSigCopies = wetSigItems
      .filter(hasPopulatedAddress)
      .reduce((sum, m) => sum + (m.copies ?? 0), 0);
    const upgradedShipping = wetSigItems.some(
      m => m.shippingTier && m.shippingTier !== ShippingTier.GROUND
    );
    wantsAdditionalAffidavit = totalWetSigCopies > 1 || upgradedShipping;
  } else {
    wantsAdditionalAffidavit = hasPopulated;
  }

  return {
    affidavitType,
    shippingSpeed,
    deliveryOption: hasPopulated ? 'mail' : 'email',
    wantsAdditionalAffidavit
  };
}

export const getRecipientValidation = getMailDeliveryValidationError;

/**
 * Presets are publisher-configured and read-only, so an incomplete one must block
 * the step: dropping it would skip a legally required mailing, and submitting it
 * would persist undeliverable mail.
 */
export function noticeTypeDefaultRecipientsInvalid(
  recipients: MailDelivery[]
): boolean {
  return recipients.some(recipient => getRecipientValidation(recipient) !== '');
}

export function getCourthouseValidation(recipient: MailDelivery) {
  if (!recipient.courtHouse) {
    return 'Courthouse is missing';
  }
  if (!recipient.copies) {
    return 'Number of copies is missing';
  }
  if (recipient.copies > MAX_MAIL_COPIES || recipient.copies < 1) {
    return 'Mailing must have copies between one and five copies';
  }
  return '';
}

export function selectIsFormComplete(
  state: AffidavitRecipientsFormState,
  activeStepId: string,
  stepId: string
) {
  const {
    sendAffidavitByMail,
    sendAffidavitToCourthouse,
    mailingRecipients,
    recipientValidationErrors,
    courthouseValidationErrors,
    showErrors
  } = state;

  // Flag-on path: completeness based on add-on selections
  if (state.affidavitType !== null) {
    if (activeStepId !== stepId) {
      return false;
    }
    if (noticeTypeDefaultRecipientsInvalid(state.noticeTypeDefaultRecipients)) {
      return false;
    }
    const needsAddress = affidavitAddressRequired(
      state.affidavitType,
      state.deliveryOption
    );
    if (needsAddress) {
      if (!mailingRecipients.some(hasPopulatedAddress)) {
        return false;
      }
    }
    return true;
  }

  if (
    sendAffidavitByMail &&
    mailingRecipients.length === 1 &&
    !hasPopulatedAddress(mailingRecipients[0])
  ) {
    return false;
  }

  if (activeStepId !== stepId) {
    return false;
  }
  if (showErrors) {
    if (sendAffidavitByMail) {
      return recipientValidationErrors.every(e => !e);
    }
    if (sendAffidavitToCourthouse) {
      return courthouseValidationErrors.every(e => !e);
    }
  }
  return true;
}

export type RecipientValidationResult = {
  errors: string[];
  populated: MailDelivery[];
  hasErrors: boolean;
};

/**
 * Validates recipients in-place (preserving original indices) and verifies
 * address deliverability only for populated rows. Returns an error array
 * aligned to the original recipients positions so the UI renders errors
 * next to the correct form row.
 */
export async function validateRecipientsWithAlignment(
  recipients: MailDelivery[],
  options: {
    disableAddressVerification: boolean;
    draft: ESnapshotExists<ENoticeDraft>;
  }
): Promise<RecipientValidationResult> {
  const errors = emptyErrors(recipients.length);
  const populatedIndices: number[] = [];
  const populated: MailDelivery[] = [];

  for (let i = 0; i < recipients.length; i++) {
    if (hasPopulatedAddress(recipients[i])) {
      populatedIndices.push(i);
      populated.push(recipients[i]);
    }
  }

  // Field validation — only for populated rows, placed at original index
  for (let j = 0; j < populated.length; j++) {
    const fieldError = getRecipientValidation(populated[j]);
    if (fieldError) {
      errors[populatedIndices[j]] = fieldError;
    }
  }

  if (errors.some(Boolean)) {
    return { errors, populated, hasErrors: true };
  }

  // Address deliverability verification
  if (!options.disableAddressVerification && populated.length > 0) {
    const results = await Promise.all(
      populated.map(mailingAddress =>
        verifyAddressDeliverability({
          mailingAddress,
          draft: options.draft
        })
      )
    );
    for (let j = 0; j < results.length; j++) {
      if (!results[j]) {
        errors[populatedIndices[j]] =
          "The address you've entered is invalid. Please input a new address to ensure delivery.";
      }
    }
  }

  return { errors, populated, hasErrors: errors.some(Boolean) };
}
